import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { applyBrand } from './whitelabel';
applyBrand();
import { ThemeProvider } from './context/ThemeContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { LanguageProvider } from './contexts/LanguageContext';
import { SchoolProvider } from './contexts/SchoolContext';
import { ConnectivityProvider } from './contexts/ConnectivityContext';
import { InstallPrompt } from './components/common/InstallPrompt';
import { clearFirestoreCacheAndReload } from './firebase';

// Manual escape hatch for a wedged local Firestore cache (see clearFirestoreCacheAndReload
// in firebase.ts) — visiting ?clearCache=1 forces a clean resync without needing to first
// hit a crash or a stuck-loading screen that offers the button. Strips the param before
// navigating so the follow-up reload doesn't re-trigger this and loop forever.
const bootParams = new URLSearchParams(window.location.search);
if (bootParams.get('clearCache') === '1') {
  bootParams.delete('clearCache');
  const cleanQuery = bootParams.toString();
  const cleanUrl = window.location.pathname + (cleanQuery ? `?${cleanQuery}` : '') + window.location.hash;
  clearFirestoreCacheAndReload(cleanUrl);
} else {

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
// /verify/{id}: the public page a certificate's QR code opens; no sign-in, none of the portal
const verifyId = window.location.pathname.match(/^\/verify\/([\w-]+)/)?.[1];
const CertificateVerify = React.lazy(() => import('./components/institution/CertificateVerify'));
if (verifyId) root.render(<React.Suspense fallback={null}><CertificateVerify id={verifyId} /></React.Suspense>);
else root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <ConnectivityProvider>
          <SchoolProvider>
            <LanguageProvider>
              <App />
              <InstallPrompt />
            </LanguageProvider>
          </SchoolProvider>
        </ConnectivityProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// Registered manually (registerType: 'autoUpdate' + injectRegister: false in vite.config.ts)
// so a new version never silently reloads a student mid-exam — it waits to be accepted.
if ('serviceWorker' in navigator) {
  // @ts-ignore — virtual module supplied by vite-plugin-pwa at build time
  import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      immediate: true,
      onNeedRefresh() {
        const accept = window.confirm('A new version of this app is available. Refresh now?');
        if (accept) window.location.reload();
      },
      onOfflineReady() {
        console.log('App ready to work offline.');
      },
    });
  }).catch(() => { /* not a PWA build (e.g. some preview/dev contexts) — safe to ignore */ });
}

}
